'use client';

import { useTranslations } from 'next-intl';
import { useActionState } from 'react';
import { Link } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackEvent } from '@/lib/analytics/plausible';
import { signUp, type SignUpState } from './actions';

const initial: SignUpState = { error: null };

type Props = { plan?: string | null };

export function SignupForm({ plan }: Props = {}) {
  const t = useTranslations('auth.signup');
  const tErrors = useTranslations('auth.signup.errors');
  const [state, action, pending] = useActionState(signUp, initial);

  const labelTemplate = t('gdpr.label');
  const privacyLinkLabel = t('gdpr.privacyLinkLabel');
  const termsLinkLabel = t('gdpr.termsLinkLabel');

  function renderGdprLabel() {
    const beforePrivacy = labelTemplate.split('%privacyLink%')[0];
    const afterPrivacyRaw = labelTemplate.split('%privacyLink%')[1] ?? '';
    const beforeTerms = afterPrivacyRaw.split('%termsLink%')[0];
    const afterTerms = afterPrivacyRaw.split('%termsLink%')[1] ?? '';
    return (
      <span>
        {beforePrivacy}
        <Link href="/privacy" className="text-emerald-700 underline" target="_blank">
          {privacyLinkLabel}
        </Link>
        {beforeTerms}
        <Link href="/terms" className="text-emerald-700 underline" target="_blank">
          {termsLinkLabel}
        </Link>
        {afterTerms}
      </span>
    );
  }

  function localizedError(code: string | null) {
    if (!code) return null;
    if (code === 'gdprRequired' || code === 'invalid') return tErrors(code);
    return code;
  }

  return (
    <form
      action={action}
      onSubmit={() => trackEvent('Signup Started', plan ? { plan } : undefined)}
      className="space-y-4"
    >
      <div className="space-y-1.5">
        <Label htmlFor="orgName">{t('orgName')}</Label>
        <Input id="orgName" name="orgName" type="text" required maxLength={120} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="email">{t('email')}</Label>
        <Input id="email" name="email" type="email" required />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="password">{t('password')}</Label>
        <Input id="password" name="password" type="password" required minLength={8} />
      </div>
      {plan ? <input type="hidden" name="plan" value={plan} /> : null}
      <div className="flex items-start gap-2">
        <input
          id="gdprConsent"
          name="gdprConsent"
          type="checkbox"
          required
          className="mt-0.5 h-4 w-4 rounded border-slate-300"
        />
        <Label htmlFor="gdprConsent" className="text-xs leading-snug font-normal">
          {renderGdprLabel()}
        </Label>
      </div>
      {state.error ? (
        <p className="text-destructive text-xs" role="alert">
          {localizedError(state.error)}
        </p>
      ) : null}
      <Button type="submit" className="w-full" disabled={pending}>
        {pending ? t('submitting') : t('submit')}
      </Button>
    </form>
  );
}
